import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Box, Alert } from '@mui/material';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from '../utils/api';
import { EDIT_TOKEN_REQUEST_SUCCESS_MESSAGE } from '../utils/constants';

const ServiceProviderEditRequestInner: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email.trim()) {
      setError('Email address is required');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!executeRecaptcha) {
      setError('reCAPTCHA not loaded. Please refresh the page and try again.');
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha('request_edit_token');
      await axios.post('/api/service-providers/request-edit-token', { email: email.trim(), recaptchaToken });
      setSuccess(true);
      setTimeout(() => {
        navigate(`/register/agency/edit?email=${encodeURIComponent(email.trim())}`);
      }, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to request passcode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Edit Your Organization Registration
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Enter your registration email address to receive a passcode.
        </Typography>

        {success && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {EDIT_TOKEN_REQUEST_SUCCESS_MESSAGE}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Registration Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading || success}
            sx={{ mb: 3 }}
            helperText="Enter the email address you used to register"
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button type="submit" variant="contained" size="large" disabled={loading || success} fullWidth sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {loading ? 'Sending...' : 'Send Passcode'}
            </Button>
            <Button variant="outlined" size="large" component={Link} to="/register/agency" disabled={loading || success} fullWidth sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Back to Registration
            </Button>
          </Box>
        </form>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Note: You can only edit your registration if your email address has been verified.
            The passcodewill expire in 1 hour and can only be used once.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

const ServiceProviderEditRequest: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <ServiceProviderEditRequestInner />
    </GoogleReCaptchaProvider>
  );
};

export default ServiceProviderEditRequest;
